import { useEffect, useState } from "react";
import { X, Wrench } from "@phosphor-icons/react";
import { ASSET_TYPE_CONFIG, STATUS_CONFIG } from "@/lib/constants";
import { LLSelect } from "@/components/ui/ll-select";
import AssetAxleEditor from "./AssetAxleEditor";
import AssetMaintenanceSection from "./AssetMaintenanceSection";

export default function AssetFormDialog({ asset, onClose, onSave }) {
  const [form, setForm] = useState({
    name: asset?.name || "",
    type: asset?.type || "truck",
    status: asset?.status || "idle",
    lat: asset?.lat || 47.6062,
    lng: asset?.lng || -122.3321,
    driver: asset?.driver || "",
    license_plate: asset?.license_plate || "",
    capacity_lbs: asset?.capacity_lbs || 0,
    current_load_lbs: asset?.current_load_lbs || 0,
    notes: asset?.notes || "",
    axles: asset?.axles || null,
    hours_meter: asset?.hours_meter || 0,
    service_interval_hours: asset?.service_interval_hours || 0,
    // Compliance metadata
    vin: asset?.vin || "",
    registration_number: asset?.registration_number || "",
    registration_expiry: asset?.registration_expiry?.slice(0, 10) || "",
    insurance_carrier: asset?.insurance_carrier || "",
    insurance_policy: asset?.insurance_policy || "",
    insurance_expiry: asset?.insurance_expiry?.slice(0, 10) || "",
    ifta_expiry: asset?.ifta_expiry?.slice(0, 10) || "",
    annual_inspection_expiry: asset?.annual_inspection_expiry?.slice(0, 10) || "",
    odometer_miles: asset?.odometer_miles || 0,
    mpg_avg: asset?.mpg_avg || 0,
  });

  const update = (key, val) => setForm({ ...form, [key]: val });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      lat: parseFloat(form.lat),
      lng: parseFloat(form.lng),
      capacity_lbs: parseInt(form.capacity_lbs) || 0,
      current_load_lbs: parseInt(form.current_load_lbs) || 0,
      hours_meter: parseInt(form.hours_meter) || 0,
      service_interval_hours: parseInt(form.service_interval_hours) || 0,
      odometer_miles: parseInt(form.odometer_miles) || null,
      mpg_avg: parseFloat(form.mpg_avg) || null,
      vin: form.vin?.trim() || null,
      registration_number: form.registration_number?.trim() || null,
      registration_expiry: form.registration_expiry || null,
      insurance_carrier: form.insurance_carrier?.trim() || null,
      insurance_policy: form.insurance_policy?.trim() || null,
      insurance_expiry: form.insurance_expiry || null,
      ifta_expiry: form.ifta_expiry || null,
      annual_inspection_expiry: form.annual_inspection_expiry || null,
      // Send null explicitly so backend $unsets the field
      axles: Array.isArray(form.axles) && form.axles.length > 0 ? form.axles : null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4" data-testid="asset-form-dialog">
      <div className="bg-[#0d0d0d] border border-[#4d4d4d] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#2a2a2a] p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">
              {asset ? "Edit" : "Create"} Asset
            </div>
            <h2 className="font-heading font-black text-xl tracking-tight mt-1">
              {asset ? form.name : "New Fleet Asset"}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 hover:bg-[#1a1a1a] flex items-center justify-center" data-testid="close-form">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-name"
            />
          </Field>

          <Field label="License Plate">
            <input
              type="text"
              value={form.license_plate}
              onChange={(e) => update("license_plate", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono uppercase"
              data-testid="form-plate"
            />
          </Field>

          <Field label="Type">
            <LLSelect
              value={form.type}
              onChange={(v) => update("type", v)}
              testId="form-type"
              className="!w-full text-sm normal-case tracking-normal"
              options={Object.entries(ASSET_TYPE_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
            />
          </Field>

          <Field label="Status">
            <LLSelect
              value={form.status}
              onChange={(v) => update("status", v)}
              testId="form-status"
              className="!w-full text-sm normal-case tracking-normal"
              options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
            />
          </Field>

          <Field label="Driver / Operator">
            <input
              type="text"
              value={form.driver}
              onChange={(e) => update("driver", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
              data-testid="form-driver"
            />
          </Field>

          <Field label="Capacity (lbs)">
            <input
              type="number"
              value={form.capacity_lbs}
              onChange={(e) => update("capacity_lbs", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-capacity"
            />
          </Field>

          <Field label="Current Load (lbs)">
            <input
              type="number"
              value={form.current_load_lbs}
              onChange={(e) => update("current_load_lbs", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-load"
            />
          </Field>

          <Field label="Latitude">
            <input
              type="number"
              step="0.0001"
              value={form.lat}
              onChange={(e) => update("lat", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-lat"
            />
          </Field>

          <Field label="Longitude">
            <input
              type="number"
              step="0.0001"
              value={form.lng}
              onChange={(e) => update("lng", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-lng"
            />
          </Field>

          <div className="md:col-span-2 border-t border-[#2a2a2a] pt-4 mt-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold mb-3">· Compliance & Documents</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="VIN">
                <input value={form.vin} onChange={(e) => update("vin", e.target.value.toUpperCase())} maxLength={17}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" data-testid="form-vin" />
              </Field>
              <Field label="Odometer (mi)">
                <input type="number" value={form.odometer_miles} onChange={(e) => update("odometer_miles", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" data-testid="form-odometer" />
              </Field>
              <Field label="Registration #">
                <input value={form.registration_number} onChange={(e) => update("registration_number", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" />
              </Field>
              <Field label="Registration Expiry">
                <input type="date" value={form.registration_expiry} onChange={(e) => update("registration_expiry", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" data-testid="form-reg-expiry" />
              </Field>
              <Field label="Insurance Carrier">
                <input value={form.insurance_carrier} onChange={(e) => update("insurance_carrier", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm" />
              </Field>
              <Field label="Insurance Policy #">
                <input value={form.insurance_policy} onChange={(e) => update("insurance_policy", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" />
              </Field>
              <Field label="Insurance Expiry">
                <input type="date" value={form.insurance_expiry} onChange={(e) => update("insurance_expiry", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" data-testid="form-ins-expiry" />
              </Field>
              <Field label="IFTA Expiry">
                <input type="date" value={form.ifta_expiry} onChange={(e) => update("ifta_expiry", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" />
              </Field>
              <Field label="Annual Inspection Expiry">
                <input type="date" value={form.annual_inspection_expiry} onChange={(e) => update("annual_inspection_expiry", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" />
              </Field>
              <Field label="MPG (avg)">
                <input type="number" step="0.1" value={form.mpg_avg} onChange={(e) => update("mpg_avg", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono" />
              </Field>
            </div>
          </div>

          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm resize-none"
                data-testid="form-notes"
              />
            </Field>
          </div>

          <AssetAxleEditor
            value={form.axles}
            onChange={(v) => update("axles", v)}
            assetTemplateId={asset?.axle_template_id}
          />

          <div className="md:col-span-2 border border-[#2a2a2a] bg-[#0d0d0d]">
            <div className="px-3 py-2 border-b border-[#2a2a2a] flex items-center gap-2">
              <Wrench size={14} className="text-[#ff8c00]" weight="bold" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">Maintenance & Hours</div>
            </div>
            <div className="p-3 grid grid-cols-2 gap-3">
              <Field label="Hour Meter">
                <input
                  type="number"
                  value={form.hours_meter}
                  onChange={(e) => update("hours_meter", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
                  data-testid="form-hours-meter"
                />
              </Field>
              <Field label="Service Interval (hours)">
                <input
                  type="number"
                  value={form.service_interval_hours}
                  onChange={(e) => update("service_interval_hours", e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
                  data-testid="form-service-interval"
                />
              </Field>
            </div>
            {asset?.id && <AssetMaintenanceSection assetId={asset.id} />}
          </div>

          <div className="md:col-span-2 flex items-center gap-3 pt-4 border-t border-[#2a2a2a]">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-[#4d4d4d] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#1a1a1a]"
              data-testid="form-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              data-testid="form-submit"
            >
              {asset ? "Save Changes" : "Create Asset"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">
        {label} {required && <span className="text-[#ff8c00]">*</span>}
      </span>
      {children}
    </label>
  );
}
